/**
 * Eine Datei herunterladen – und den Speicherort wählen lassen, wo es geht.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was ein Browser hier erlaubt und was nicht
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Eine Webanwendung kann keinen Ordner auf einem fremden Rechner bestimmen.
 * Das ist keine Lücke, sondern die Grenze des Browsers: Eine Seite, die ohne
 * Rückfrage irgendwohin schreiben dürfte, wäre ein Werkzeug für jede andere
 * Seite auch.
 *
 * Was es gibt, ist `showSaveFilePicker()` – der native «Speichern unter …»
 * Dialog, den DER BENUTZER bedient. Er steht in Chrome, Edge und Opera auf
 * dem Schreibtisch. Firefox hat ihn nicht; Safari hat ihn nicht; auf iOS und
 * Android gibt es ihn nicht.
 *
 * Deshalb zwei Wege, und der zweite ist kein Notbehelf:
 *
 *   1  Wo es den Dialog gibt: öffnen. Der Betrieb wählt Ordner und Namen
 *      selbst, wie in jedem anderen Programm.
 *
 *   2  Wo nicht: der gewöhnliche Download. Der Browser legt die Datei dort
 *      ab, wo der Benutzer das in SEINEN Einstellungen bestimmt hat – und
 *      auf dem Telefon öffnet sich der Speichern- und Teilen-Dialog des
 *      Systems. Auch das ist eine Wahl, nur eine, die der Browser führt.
 *
 * Was diese Datei ausdrücklich NICHT tut: einen eigenen Dateimanager
 * aufmachen, einen Pfad in den Einstellungen verlangen oder behaupten, sie
 * könne auf einem iPhone einen Ordner ansteuern.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Und sie erzeugt nichts
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Ein Download ist ein GET auf eine bestehende Datei. Er legt keinen Beleg
 * an, vergibt keine Nummer und ändert keinen Zustand – weder hier noch am
 * Server. Das gilt für beide Wege gleich.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Und sie spricht nicht selbst mit dem Netz
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Hier stand ein eigenes `fetch`. Das ist in dieser Anwendung genau eine
 * Zeile zu viel: `net/apiClient.ts` ist die einzige Netzwerkstelle, und ein
 * Pflichttest hält das fest (`tests/web-single-fetch.test.ts`). Er hat
 * diesen Verstoss gefunden, bevor er ausgeliefert war.
 *
 * Deshalb bekommt `speichereDatei` die Bytes von aussen: `laden` ist ein
 * benannter Aufruf des Clients (`api.dateiLaden`, `api.betreuungMonatPdfsLaden`).
 * Aufgerufen wird er nur auf dem Dialogweg – der gewöhnliche Download lädt
 * im Browser, ohne dass eine Datei durch den Speicher der Seite geht.
 */

/** Hat dieser Browser den nativen «Speichern unter …» Dialog? */
export function speichernDialogVerfuegbar(): boolean {
  return typeof (window as { showSaveFilePicker?: unknown }).showSaveFilePicker === 'function'
}

interface SaveFilePicker {
  showSaveFilePicker: (optionen: {
    suggestedName?: string
    types?: Array<{ description: string; accept: Record<string, string[]> }>
  }) => Promise<{
    createWritable: () => Promise<{
      write: (daten: Blob) => Promise<void>
      close: () => Promise<void>
    }>
  }>
}

/**
 * Einen Dateinamen bauen, den ein Mensch wiederfindet.
 *
 * «Rechnung 2026-0042 Lina Muster Dezember 2026.pdf» statt «download.pdf».
 * Was kein Dateiname sein darf, wird ersetzt – ein Kundenname mit
 * Schrägstrich hat schon einmal einen Ordner erzeugt (siehe das ZIP in
 * `routes/betreuung.ts`).
 */
export function dateiname(teile: Array<string | null | undefined>, endung: string): string {
  const sauber = teile
    .map((t) => (t ?? '').replace(/[^\p{L}\p{N} .\-_]/gu, '-').trim())
    .filter((t) => t !== '')
    .join(' ')
    .replace(/\s+/g, ' ')
    .slice(0, 120)
  return `${sauber || 'Datei'}.${endung}`
}

/**
 * Herunterladen – mit Dialog, wo der Browser ihn hat.
 *
 * `url` ist die Adresse für den gewöhnlichen Download, `name` der Vorschlag
 * im Dialog und der Dateiname danach, `laden` holt die Bytes für den
 * Dialogweg. Der Rückgabewert sagt, welcher Weg gegangen wurde; die
 * Oberfläche braucht ihn nicht, die Prüfung schon.
 */
export async function speichereDatei(
  url: string,
  name: string,
  laden: () => Promise<Blob>
): Promise<'dialog' | 'download' | 'abgebrochen'> {
  if (speichernDialogVerfuegbar()) {
    try {
      const griff = await (window as unknown as SaveFilePicker).showSaveFilePicker({
        suggestedName: name,
        types: name.toLowerCase().endsWith('.pdf')
          ? [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }]
          : name.toLowerCase().endsWith('.zip')
            ? [{ description: 'ZIP-Archiv', accept: { 'application/zip': ['.zip'] } }]
            : undefined
      })
      /*
        Erst den Dialog, dann laden.

        Umgekehrt hinge der Betrieb vor einem Dialog, der erst nach dem
        Herunterladen von dreissig PDFs erscheint – und bräche er ab, wäre
        die Übertragung umsonst gewesen.
      */
      const daten = await laden()
      const schreiber = await griff.createWritable()
      await schreiber.write(daten)
      await schreiber.close()
      return 'dialog'
    } catch (e) {
      /*
        «AbortError» heisst: Der Benutzer hat den Dialog geschlossen. Das ist
        keine Störung und bekommt keine Fehlermeldung – es ist eine Antwort.
      */
      if (e instanceof DOMException && e.name === 'AbortError') return 'abgebrochen'
      /* Alles andere: der gewöhnliche Weg, statt gar nichts. */
    }
  }

  /*
    Der gewöhnliche Download. `a.download` nennt den Dateinamen; wohin die
    Datei kommt, bestimmen die Einstellungen des Browsers – auf dem Telefon
    der Speichern- und Teilen-Dialog des Systems.
  */
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.rel = 'noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
  return 'download'
}
